import React, { Component } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Tooltip,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { GROUPS, REGIME_LABELS, STATUS_LABELS, groupOf, titleOf } from './Agents';

// Comparaison de deux consignes de modération, côte à côte (B5_02 — contrepartie de DA18 :
// pas de modèle maître en v1, mais un écran qui rend la dérive VISIBLE).
//
// ⛔ LECTURE SEULE. Cet écran n'édite rien : il compare et il montre. La donnée est celle de
//    CD3 (`Agents.state.data`), reçue en props — aucun fetch, aucune route, aucun service.
//
// Deux fonctions PURES exportées — c'est le CONTRAT consommé par B5_01 (D22, arête
// `B5_02 -> B5_01`), testé dans src/tests/promptDiff.test.js :
//   diffPromptLines(textA, textB, options?)  → diff ligne à ligne + compteurs
//   buildDiffAgentOptions(data)              → inventaire du sélecteur (entrées + disabled + raison)
//
// ⛔ diffPromptLines compare TOUT, au caractère près (décision transverse 2026-08-27) : B5_01
//    s'en sert pour expliquer un refus de publication, que le serveur décide au caractère près.
//    L'option `ignoreForm` est un confort d'AFFICHAGE de cet écran ; l'appel nu reste strict.
//
// ⛔ Aucune bibliothèque de diff : `diff-sequences` / `jest-diff` existent dans node_modules en
//    transitives de react-scripts, les importer serait une dépendance non déclarée.
//
// ⚠ Import circulaire ASSUMÉ avec ./Agents (Agents monte <PromptDiff/>, PromptDiff nomme les
//   agents avec les helpers d'Agents). Il est sûr parce que rien n'est appelé au chargement du
//   module : GROUPS/titleOf/… ne sont lus qu'à l'exécution (inventaire, rendu). Recopier la table
//   des titres ici serait la « seconde dérivation qui diverge au premier changement ».

const ROW_EQUAL = 'equal'; // ligne identique des deux côtés
const ROW_FORM = 'form'; // identique une fois la forme neutralisée (option ignoreForm seulement)
const ROW_CHANGE = 'change'; // gauche retirée et/ou droite ajoutée (trou rembourré par null)

// Sous ce seuil (= breakpoint `md` de MUI, 900 px, exclu) la grille passe en une colonne.
const NARROW_QUERY = '(max-width: 899.95px)';

// ─────────────────────────────────────────────────────────────────────────────
// Diff ligne à ligne — plus grande sous-séquence commune (LCS) sur les lignes.
// Mesuré sur les vraies consignes (276 × 327 lignes = 90 k cellules) : 0,07 à 1,5 ms par couple.
// ─────────────────────────────────────────────────────────────────────────────

// Découpage sur `\n` SEULEMENT : un `\r` résiduel resterait dans la ligne et compterait comme
// une différence — c'est voulu, la fonction ne masque rien. Texte vide → aucune ligne (et non
// une ligne vide), sinon deux consignes absentes seraient « 100 % identiques ».
function splitLines(text) {
  if (typeof text !== 'string' || text === '') return [];
  return text.split('\n');
}

// Neutralise la FORME d'une ligne pour l'option d'affichage « ignorer les différences de forme ».
// Cas réels (mesurés) : GD écrit `19  : 'Violation des droits d\'auteur';` là où GC écrit
// `19 : Violation des droits d'auteur` ; et une apostrophe typographique (`d’ANALYSE`) suffit à
// faire diverger la première ligne du couple GD sujets. Sur les 3 couples intra-produit cette
// normalisation ne touche que 0 à 1 ligne ; sur GC ↔ GD elle en requalifie 38.
function normalizeForm(line) {
  return line
    .replace(/\\'/g, "'") // apostrophes échappées
    .replace(/[‘’ʼ]/g, "'") // apostrophes typographiques
    .replace(/[“”«»]/g, '"') // guillemets typographiques
    .replace(/["'`]/g, '') // toute forme de guillemet
    .replace(/[;,]\s*$/, '') // ponctuation de fin de ligne
    .replace(/\s+/g, ' ')
    .trim();
}

// Table LCS en Int32Array (une seule allocation), puis parcours avant pour émettre la suite
// d'opérations dans l'ordre des deux textes. `keyOf` (optionnel) est la clé de comparaison ;
// sans lui, la clé est la ligne elle-même (strict).
function lcsOps(a, b, keyOf) {
  const n = a.length;
  const m = b.length;
  const ka = keyOf ? a.map(keyOf) : a;
  const kb = keyOf ? b.map(keyOf) : b;
  const W = m + 1;
  const T = new Int32Array((n + 1) * W);
  for (let i = n - 1; i >= 0; i -= 1) {
    for (let j = m - 1; j >= 0; j -= 1) {
      T[i * W + j] =
        ka[i] === kb[j]
          ? T[(i + 1) * W + j + 1] + 1
          : Math.max(T[(i + 1) * W + j], T[i * W + j + 1]);
    }
  }

  const ops = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (ka[i] === kb[j]) {
      ops.push({ t: a[i] === b[j] ? ROW_EQUAL : ROW_FORM, l: i, r: j });
      i += 1;
      j += 1;
    } else if (T[(i + 1) * W + j] >= T[i * W + j + 1]) {
      ops.push({ t: 'removed', l: i });
      i += 1;
    } else {
      ops.push({ t: 'added', r: j });
      j += 1;
    }
  }
  while (i < n) {
    ops.push({ t: 'removed', l: i });
    i += 1;
  }
  while (j < m) {
    ops.push({ t: 'added', r: j });
    j += 1;
  }
  return ops;
}

// Regroupe les opérations en COUPLES DE LIGNES (une ligne de grille = un couple) : un bloc
// contigu de retraits/ajouts devient max(k, m) couples, le côté manquant rembourré par `null`.
// C'est ce rembourrage qui fait tenir l'alignement gauche/droite. Chaque bloc changé porte un
// numéro de « divergence » (hunk) pour la navigation.
function pairRows(ops, a, b) {
  const rows = [];
  let hunks = 0;
  let i = 0;
  while (i < ops.length) {
    const op = ops[i];
    if (op.t === ROW_EQUAL || op.t === ROW_FORM) {
      rows.push({
        kind: op.t,
        left: a[op.l],
        right: b[op.r],
        leftNo: op.l + 1,
        rightNo: op.r + 1,
        hunk: null
      });
      i += 1;
    } else {
      const removedIdx = [];
      const addedIdx = [];
      while (i < ops.length && (ops[i].t === 'removed' || ops[i].t === 'added')) {
        if (ops[i].t === 'removed') removedIdx.push(ops[i].l);
        else addedIdx.push(ops[i].r);
        i += 1;
      }
      hunks += 1;
      const len = Math.max(removedIdx.length, addedIdx.length);
      for (let k = 0; k < len; k += 1) {
        const l = k < removedIdx.length ? removedIdx[k] : null;
        const r = k < addedIdx.length ? addedIdx[k] : null;
        rows.push({
          kind: ROW_CHANGE,
          left: l === null ? null : a[l],
          right: r === null ? null : b[r],
          leftNo: l === null ? null : l + 1,
          rightNo: r === null ? null : r + 1,
          hunk: hunks
        });
      }
    }
  }
  return { rows, hunks };
}

/**
 * Diff ligne à ligne de deux consignes. Fonction PURE — contrat consommé par B5_01 (D22).
 *
 * @param {?string} textA consigne de gauche (texte complet, CD3 `instructions`)
 * @param {?string} textB consigne de droite
 * @param {{ignoreForm?: boolean}} [options] `ignoreForm` : comparer sur la ligne neutralisée
 *        (normalizeForm) — les couples égaux-neutralisés mais différents au caractère près sont
 *        typés `form` et comptés À PART (`formOnly`). ⛔ Sans option : strict, rien n'est masqué.
 * @returns {{rows: Array<{kind: string, left: ?string, right: ?string, leftNo: ?number,
 *   rightNo: ?number, hunk: ?number}>, hunks: number, stats: {linesA: number, linesB: number,
 *   common: number, formOnly: number, removed: number, added: number, ratio: ?number}}}
 *   `common` = couples appariés comme communs (strict : identiques ; avec `ignoreForm` :
 *   identiques OU en forme seule) · `ratio` = 2·common / (linesA + linesB) — « lignes communes ÷
 *   moyenne des longueurs » (décision L4) ; `null` si les deux textes sont vides.
 */
export function diffPromptLines(textA, textB, options) {
  const opts = options || {};
  const a = splitLines(textA);
  const b = splitLines(textB);
  const ops = lcsOps(a, b, opts.ignoreForm ? normalizeForm : null);
  const { rows, hunks } = pairRows(ops, a, b);

  let equal = 0;
  let form = 0;
  let removed = 0;
  let added = 0;
  rows.forEach((row) => {
    if (row.kind === ROW_EQUAL) equal += 1;
    else if (row.kind === ROW_FORM) form += 1;
    else {
      if (row.left !== null) removed += 1;
      if (row.right !== null) added += 1;
    }
  });

  const common = equal + form;
  const total = a.length + b.length;
  return {
    rows,
    hunks,
    stats: {
      linesA: a.length,
      linesB: b.length,
      common,
      formOnly: form,
      removed,
      added,
      ratio: total === 0 ? null : (2 * common) / total
    }
  };
}

// La raison d'inéligibilité se choisit selon le STATUT ; l'inéligibilité elle-même, jamais
// (voir buildDiffAgentOptions). Libellés réutilisés d'Agents.jsx, pas recopiés.
function ineligibilityReason(agent) {
  if (agent.status === 'missing' || agent.status === 'unreadable') {
    return STATUS_LABELS[agent.status];
  }
  if (agent.status === 'ok') return 'Lu, mais aucune consigne renvoyée';
  return `État inconnu (« ${agent.status} »)`;
}

/**
 * Inventaire du sélecteur. Fonction PURE — contrat consommé par B5_01 (D22).
 *
 * ⛔ L'inéligibilité se calcule sur l'ABSENCE DE CONSIGNE, jamais sur le statut : une entrée
 *    `status:'ok'` peut avoir `instructions:null` (isUsableAgentPayload ne valide que name + id).
 *    Griser sur le seul statut la rendrait sélectionnable → diff « tout retiré », ou « 100 %
 *    identiques » face à un autre vide.
 * ⛔ Une entrée inéligible reste PRÉSENTE (grisée, avec sa raison), jamais absente : une liste
 *    raccourcie en silence est le mode de panne que CD3+ existe pour empêcher.
 * ⚠ Prend le payload CD3 ENTIER (`{agents, context_agent}`), pas `agents[]` seul : c'est la
 *   fonction qui décide de ce qu'elle liste, la signature ne dépend donc pas de cette décision.
 *   Décision L5 (2026-08-28) : l'agent de contexte n'est PAS listé — il ne rend aucun verdict,
 *   le comparer à une consigne de jugement n'a pas de sens (D8, DA21) ; l'écran le dit en légende.
 *
 * @param {?{agents: Array<object>, context_agent: ?object}} data payload CD3
 * @returns {Array<{id: string, label: string, group: ?string, regime: ?string, status: string,
 *   version: ?number, disabled: boolean, reason: ?string}>} dans l'ordre de CD3 (l'ordre fait foi)
 */
export function buildDiffAgentOptions(data) {
  const agents = data && Array.isArray(data.agents) ? data.agents : [];
  return agents.map((agent) => {
    const group = groupOf(agent);
    const readable = typeof agent.instructions === 'string' && agent.instructions.length > 0;
    return {
      id: agent.id,
      label: titleOf(agent, false),
      group: group ? group.label : null,
      regime: agent.regime === undefined ? null : agent.regime,
      status: agent.status,
      version:
        agent.version_active === null || agent.version_active === undefined
          ? null
          : agent.version_active,
      disabled: !readable,
      reason: readable ? null : ineligibilityReason(agent)
    };
  });
}

// Couples proposés, calculés STRUCTURELLEMENT (groupe + régime), jamais sur les ids — les slugs
// des ids mentent (untitled-agent = GC stricte). Deux familles (décisions L2/L3) :
//   alike  : stricte ↔ doute d'un même groupe — « doivent se ressembler »
//   across : strictes de deux groupes différents, en PAIRES — « entre surfaces, écart attendu »
function proposedCouples(entries) {
  const byGroup = {};
  entries.forEach((entry) => {
    if (!entry.group || !entry.regime) return;
    if (!byGroup[entry.group]) byGroup[entry.group] = {};
    byGroup[entry.group][entry.regime] = entry;
  });
  const labels = Object.keys(GROUPS)
    .map((key) => GROUPS[key].label)
    .filter((label) => byGroup[label]);

  const alike = [];
  labels.forEach((label) => {
    const s = byGroup[label].strict;
    const d = byGroup[label].doubt;
    if (s && d) alike.push({ key: `alike:${label}`, label: `${label} : stricte ↔ doute`, a: s, b: d });
  });

  const across = [];
  for (let i = 0; i < labels.length; i += 1) {
    for (let j = i + 1; j < labels.length; j += 1) {
      const s1 = byGroup[labels[i]].strict;
      const s2 = byGroup[labels[j]].strict;
      if (s1 && s2) {
        across.push({
          key: `across:${labels[i]}:${labels[j]}`,
          label: `${labels[i]} ↔ ${labels[j]} (strictes)`,
          a: s1,
          b: s2
        });
      }
    }
  }
  return { alike, across };
}

// Vue en UNE colonne (écran étroit) : les couples d'un même bloc changé sont dépliés en
// « toutes les lignes retirées, puis toutes les ajoutées » — la lecture habituelle d'un diff.
// Une ligne « forme seule » est émise DEUX fois (A puis B) : la différence de forme est la seule
// information de ce typage, la réduire au côté A la ferait disparaître sous 900 px.
function toUnified(rows) {
  const out = [];
  let i = 0;
  while (i < rows.length) {
    const row = rows[i];
    if (row.kind === ROW_FORM) {
      out.push({ kind: ROW_FORM, no: `A${row.leftNo}`, text: row.left, hunk: null });
      out.push({ kind: ROW_FORM, no: `B${row.rightNo}`, text: row.right, hunk: null });
      i += 1;
    } else if (row.kind !== ROW_CHANGE) {
      out.push({ kind: row.kind, no: row.leftNo, text: row.left, hunk: null });
      i += 1;
    } else {
      const { hunk } = row;
      const lefts = [];
      const rights = [];
      while (i < rows.length && rows[i].kind === ROW_CHANGE && rows[i].hunk === hunk) {
        if (rows[i].left !== null) lefts.push(rows[i]);
        if (rows[i].right !== null) rights.push(rows[i]);
        i += 1;
      }
      lefts.forEach((r) => out.push({ kind: 'removed', no: r.leftNo, text: r.left, hunk }));
      rights.forEach((r) => out.push({ kind: 'added', no: r.rightNo, text: r.right, hunk }));
    }
  }
  return out;
}

function formatPercent(ratio) {
  if (ratio === null || ratio === undefined) return '—';
  return `${(ratio * 100).toLocaleString('fr-FR', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1
  })} %`;
}

function formatNumber(value) {
  return value.toLocaleString('fr-FR', { maximumFractionDigits: 1 });
}

// L4 — le pourcentage porte sa DÉFINITION, jamais un chiffre nu. Et il compte des LIGNES : deux
// textes peuvent dire la même chose avec des mots différents, et l'inverse — l'indicateur trie,
// il ne juge pas.
function summaryText(prefix, stats) {
  const mean = (stats.linesA + stats.linesB) / 2;
  const commonLabel =
    stats.formOnly > 0
      ? `${stats.common} lignes communes (dont ${stats.formOnly} en forme seulement)`
      : `${stats.common} lignes communes`;
  return (
    `${prefix} ${commonLabel} · ${stats.removed} retirées · ${stats.added} ajoutées — ` +
    `${formatPercent(stats.ratio)} de lignes communes` +
    (stats.ratio === null ? '' : ` (${stats.common} ÷ moyenne des longueurs ${formatNumber(mean)})`)
  );
}

// Couleurs de cellules : constantes rgba plutôt que le thème, pour rester lisibles sur le fond
// `action.hover` déjà utilisé par les fiches de prompts.
const BG_REMOVED = 'rgba(211, 47, 47, 0.12)';
const BG_ADDED = 'rgba(46, 125, 50, 0.14)';
const BG_FORM = 'rgba(237, 108, 2, 0.14)';
const BG_PAD = 'rgba(0, 0, 0, 0.03)';

const TABLE_SX = {
  width: '100%',
  tableLayout: 'fixed',
  borderCollapse: 'collapse',
  fontFamily: 'monospace',
  fontSize: '0.75rem',
  '& th': {
    textAlign: 'left',
    fontWeight: 600,
    px: 1,
    py: 0.5,
    borderBottom: '1px solid',
    borderColor: 'divider',
    position: 'sticky',
    top: 0,
    bgcolor: 'background.paper',
    zIndex: 1
  },
  '& td': {
    verticalAlign: 'top',
    px: 1,
    py: 0.125,
    borderBottom: '1px solid',
    borderColor: 'divider'
  },
  '& td.no': { color: 'text.disabled', textAlign: 'right', userSelect: 'none' },
  // T1 (décision 2026-08-28) : retour à la ligne AUTORISÉ — dans une ligne de table les deux
  // cellules partagent la hauteur, l'alignement est garanti par construction, et une ligne de
  // 936 caractères se lit sans défilement horizontal de six écrans.
  '& td.txt': { whiteSpace: 'pre-wrap', wordBreak: 'break-word', overflowWrap: 'anywhere' },
  '& td.txt:empty::after': { content: '"\\00a0"' },
  '& td.removed': { bgcolor: BG_REMOVED },
  '& td.added': { bgcolor: BG_ADDED },
  '& td.form': { bgcolor: BG_FORM },
  '& td.pad': { bgcolor: BG_PAD },
  '& tr.cursor td': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: -2 }
};

class PromptDiff extends Component {
  constructor(props) {
    super(props);
    const options = buildDiffAgentOptions(props.data);
    const { alike } = proposedCouples(options);
    // « Proposer d'emblée » : le premier couple « qui doit se ressembler » est présélectionné.
    const first = alike.find((c) => !c.a.disabled && !c.b.disabled) || null;
    const readable = options.filter((o) => !o.disabled);
    this.state = {
      leftId: first ? first.a.id : readable[0] ? readable[0].id : '',
      rightId: first ? first.b.id : readable[1] ? readable[1].id : '',
      ignoreForm: false,
      narrow: false,
      cursor: 0
    };
    this.tableRef = React.createRef();
    this._memo = { key: null, value: null };
    this.onLeftChange = this.onLeftChange.bind(this);
    this.onRightChange = this.onRightChange.bind(this);
    this.onToggleForm = this.onToggleForm.bind(this);
    this.onPrevHunk = this.onPrevHunk.bind(this);
    this.onNextHunk = this.onNextHunk.bind(this);
    this.onMediaChange = this.onMediaChange.bind(this);
  }

  // Repli une colonne sous `md` : matchMedia en cycle de vie de classe (convention super-admin :
  // pas de hook, donc pas de useMediaQuery). Gardé : jsdom (tests) n'implémente pas matchMedia.
  componentDidMount() {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    this.mql = window.matchMedia(NARROW_QUERY);
    if (this.mql.addEventListener) this.mql.addEventListener('change', this.onMediaChange);
    else if (this.mql.addListener) this.mql.addListener(this.onMediaChange);
    this.setState({ narrow: this.mql.matches });
  }

  componentWillUnmount() {
    if (!this.mql) return;
    if (this.mql.removeEventListener) this.mql.removeEventListener('change', this.onMediaChange);
    else if (this.mql.removeListener) this.mql.removeListener(this.onMediaChange);
  }

  onMediaChange(e) {
    this.setState({ narrow: e.matches });
  }

  onLeftChange(e) {
    this.setState({ leftId: e.target.value, cursor: 0 });
  }

  onRightChange(e) {
    this.setState({ rightId: e.target.value, cursor: 0 });
  }

  onToggleForm(e) {
    this.setState({ ignoreForm: e.target.checked, cursor: 0 });
  }

  selectCouple(couple) {
    this.setState({ leftId: couple.a.id, rightId: couple.b.id, cursor: 0 });
  }

  // Mémoïsé sur (idA, idB, fetched_at, mode) : le diff ne se recalcule pas à chaque rendu, et se
  // recalcule bien quand « Rafraîchir » a relu les consignes (fetched_at change).
  getDiff() {
    const { data } = this.props;
    const { leftId, rightId, ignoreForm } = this.state;
    const agents = data && Array.isArray(data.agents) ? data.agents : [];
    const left = agents.find((agent) => agent.id === leftId) || null;
    const right = agents.find((agent) => agent.id === rightId) || null;
    if (!left || !right || !left.instructions || !right.instructions) return null;

    const key = [leftId, rightId, data.fetched_at || '', ignoreForm ? 1 : 0].join('|');
    if (this._memo.key === key) return this._memo.value;

    // Le strict est TOUJOURS calculé et affiché : l'option de forme s'ajoute, elle ne remplace pas.
    const strict = diffPromptLines(left.instructions, right.instructions);
    const tolerant = ignoreForm
      ? diffPromptLines(left.instructions, right.instructions, { ignoreForm: true })
      : null;
    const value = { left, right, strict, tolerant, shown: tolerant || strict };
    this._memo = { key, value };
    return value;
  }

  goToHunk(target) {
    const diff = this.getDiff();
    if (!diff || diff.shown.hunks === 0) return;
    const k = Math.min(Math.max(target, 1), diff.shown.hunks);
    this.setState({ cursor: k }, () => {
      const root = this.tableRef.current;
      const el = root && root.querySelector(`[data-hunk="${k}"]`);
      if (el && typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ block: 'center' });
      }
    });
  }

  onPrevHunk() {
    this.goToHunk(this.state.cursor <= 1 ? 1 : this.state.cursor - 1);
  }

  onNextHunk() {
    this.goToHunk(this.state.cursor + 1);
  }

  renderSelect(testId, label, value, onChange, options) {
    const labelId = `${testId}-label`;
    return (
      <FormControl size="small" fullWidth>
        <InputLabel id={labelId}>{label}</InputLabel>
        <Select
          label={label}
          labelId={labelId}
          value={value}
          onChange={onChange}
          // Le champ fermé n'affiche qu'une ligne : le MenuItem à deux lignes ne se replie pas
          // dans un Select `size="small"`.
          renderValue={(selected) => {
            const option = options.find((o) => o.id === selected);
            if (!option) return '';
            return option.version !== null ? `${option.label} · v${option.version}` : option.label;
          }}
          // Le testid doit vivre sur le <div role="combobox"> (l'élément réellement cliqué) :
          // SelectDisplayProps y est spread (SelectInput.js:454). Sur la racine ou l'input caché,
          // gc-recette-ui cliquerait à côté.
          SelectDisplayProps={{ 'data-testid': testId }}
          // Menu SANS animation d'ouverture : pendant le Grow (~225 ms) la boîte des <li> est
          // encore en cours de mise à l'échelle et un clic automatisé (recette) atterrit sur
          // l'item voisin — constaté le 2026-08-28. Instantané pour l'humain, déterministe
          // pour l'automate.
          MenuProps={{ transitionDuration: 0 }}
        >
          {options.map((option) => (
            // MUI pose `data-value` sur le <li> (SelectInput.js:387) : c'est l'ancre de recette.
            <MenuItem key={option.id} value={option.id} disabled={option.disabled}>
              <Box>
                <Typography variant="body2">
                  {option.label}
                  {option.version !== null ? ` · v${option.version}` : ''}
                </Typography>
                <Typography variant="caption" color={option.disabled ? 'error' : 'text.secondary'}>
                  {option.disabled
                    ? `Non comparable — ${option.reason}`
                    : [option.group, REGIME_LABELS[option.regime] || option.regime]
                        .filter(Boolean)
                        .join(' · ')}
                </Typography>
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  }

  renderCouples(title, couples, testId, hint) {
    if (!couples.length) return null;
    const { leftId, rightId } = this.state;
    return (
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <Typography variant="body2" color="text.secondary">
          {title}
        </Typography>
        {couples.map((couple) => {
          const disabled = couple.a.disabled || couple.b.disabled;
          const selected =
            (leftId === couple.a.id && rightId === couple.b.id) ||
            (leftId === couple.b.id && rightId === couple.a.id);
          const chip = (
            <Chip
              size="small"
              label={couple.label}
              color={selected ? 'primary' : 'default'}
              variant={selected ? 'filled' : 'outlined'}
              disabled={disabled}
              onClick={disabled ? undefined : () => this.selectCouple(couple)}
              data-testid={testId}
            />
          );
          if (!disabled) return <span key={couple.key}>{chip}</span>;
          const reason = [couple.a, couple.b]
            .filter((entry) => entry.disabled)
            .map((entry) => `${entry.label} : ${entry.reason}`)
            .join(' · ');
          // Un Chip désactivé n'émet pas d'événements : le Tooltip a besoin d'un porteur.
          return (
            <Tooltip key={couple.key} title={reason}>
              <span>{chip}</span>
            </Tooltip>
          );
        })}
        {hint && (
          <Typography variant="caption" color="text.secondary" data-testid={`${testId}-note`}>
            {hint}
          </Typography>
        )}
      </Stack>
    );
  }

  renderSummary(diff) {
    const { ignoreForm, cursor } = this.state;
    const { strict, tolerant, shown } = diff;
    return (
      <Paper variant="outlined" sx={{ p: 1.5 }}>
        {/* UN SEUL nœud porte les compteurs — choix de lisibilité (le libellé porte sa
            définition), et ancre de recette : `count:1` + `nonempty` + fragment de texte. */}
        <Box data-testid="prompt-diff-summary">
          <Typography variant="body2">{summaryText('Au caractère près :', strict.stats)}</Typography>
          {tolerant && (
            <Typography variant="body2">
              {summaryText('En ignorant la forme :', tolerant.stats)}
            </Typography>
          )}
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          Compte des lignes, pas du sens : deux lignes peuvent dire la même chose avec des mots
          différents, et l'inverse. A : {strict.stats.linesA} lignes · B : {strict.stats.linesB}{' '}
          lignes.
        </Typography>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          sx={{ mt: 1 }}
        >
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={ignoreForm}
                onChange={this.onToggleForm}
                inputProps={{ 'data-testid': 'prompt-diff-ignore-form' }}
              />
            }
            label="Ignorer les différences de forme (apostrophes, guillemets, ponctuation de fin, espaces)"
          />
          <Stack direction="row" spacing={1} alignItems="center" data-testid="prompt-diff-nav">
            <Typography variant="body2">
              {shown.hunks === 0
                ? 'Aucune divergence'
                : `Divergence ${cursor === 0 ? '–' : cursor} / ${shown.hunks}`}
            </Typography>
            <Button size="small" variant="outlined" disabled={shown.hunks === 0} onClick={this.onPrevHunk}>
              Précédente
            </Button>
            <Button size="small" variant="outlined" disabled={shown.hunks === 0} onClick={this.onNextHunk}>
              Suivante
            </Button>
          </Stack>
        </Stack>
      </Paper>
    );
  }

  renderWideTable(diff) {
    const { cursor } = this.state;
    const { left, right, shown } = diff;
    return (
      <Box component="table" sx={TABLE_SX} data-testid="prompt-diff-table">
        <colgroup>
          <col style={{ width: 52 }} />
          <col />
          <col style={{ width: 52 }} />
          <col />
        </colgroup>
        <thead>
          <tr>
            <th colSpan={2}>A — {titleOf(left, false)}</th>
            <th colSpan={2}>B — {titleOf(right, false)}</th>
          </tr>
        </thead>
        <tbody>
          {shown.rows.map((row, index) => {
            const isForm = row.kind === ROW_FORM;
            const isChange = row.kind === ROW_CHANGE;
            const leftClass = isForm ? 'form' : isChange ? (row.left === null ? 'pad' : 'removed') : '';
            const rightClass = isForm ? 'form' : isChange ? (row.right === null ? 'pad' : 'added') : '';
            return (
              <tr
                key={index}
                data-hunk={row.hunk === null ? undefined : row.hunk}
                className={row.hunk !== null && row.hunk === cursor ? 'cursor' : undefined}
              >
                <td className={`no ${leftClass}`}>{row.leftNo === null ? '' : row.leftNo}</td>
                <td className={`txt ${leftClass}`}>{row.left === null ? '' : row.left}</td>
                <td className={`no ${rightClass}`}>{row.rightNo === null ? '' : row.rightNo}</td>
                <td className={`txt ${rightClass}`}>{row.right === null ? '' : row.right}</td>
              </tr>
            );
          })}
        </tbody>
      </Box>
    );
  }

  renderNarrowTable(diff) {
    const { cursor } = this.state;
    const { left, right, shown } = diff;
    const lines = toUnified(shown.rows);
    return (
      <Box component="table" sx={TABLE_SX} data-testid="prompt-diff-table">
        <colgroup>
          <col style={{ width: 52 }} />
          <col />
        </colgroup>
        <thead>
          <tr>
            <th colSpan={2}>
              A — {titleOf(left, false)} (retiré) · B — {titleOf(right, false)} (ajouté)
            </th>
          </tr>
        </thead>
        <tbody>
          {lines.map((line, index) => {
            const cls = line.kind === ROW_EQUAL ? '' : line.kind;
            return (
              <tr
                key={index}
                data-hunk={line.hunk === null ? undefined : line.hunk}
                className={line.hunk !== null && line.hunk === cursor ? 'cursor' : undefined}
              >
                <td className={`no ${cls}`}>{line.no}</td>
                <td className={`txt ${cls}`}>{line.text}</td>
              </tr>
            );
          })}
        </tbody>
      </Box>
    );
  }

  render() {
    // `fetchedAt` arrive DÉJÀ FORMATÉ par Agents.jsx (son formateur reste module-privé : le
    // contrat est à 7 exports, pas 8) — le libellé de l'onglet est dupliqué ici, pas déplacé.
    const { open, onClose, data, fetchedAt } = this.props;
    const { leftId, rightId, narrow } = this.state;
    const options = buildDiffAgentOptions(data);
    const readable = options.filter((option) => !option.disabled);
    const { alike, across } = proposedCouples(options);
    // Dialogue fermé : rien à calculer ni à rendre (Dialog ne monte pas son contenu).
    const diff = open ? this.getDiff() : null;

    return (
      <Dialog fullScreen open={Boolean(open)} onClose={onClose} aria-labelledby="prompt-diff-title">
        <DialogTitle id="prompt-diff-title" sx={{ pr: 7 }}>
          Comparer les consignes
          <IconButton
            aria-label="Fermer"
            onClick={onClose}
            data-testid="prompt-diff-close"
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        {/* Colonne flex : tout ce qui précède le tableau reste visible, et le tableau est LA zone
            défilante (les deux axes) — c'est ce qui rend son en-tête `sticky` réellement actif. Si
            l'écran est trop bas pour tout afficher, DialogContent (overflow auto par défaut) prend
            le relais plutôt que de rogner. */}
        <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column' }}>
          <Stack spacing={2} sx={{ flex: 1, minHeight: 0 }}>
            {/* CD3+ : les consignes bougent chez le fournisseur sans trace de notre côté — le
                dialogue plein écran masque le rappel de l'onglet, on le reporte ici. L5 : l'absence
                de l'agent de contexte dans le sélecteur est DITE, inconditionnellement. */}
            <Typography variant="caption" color="text.secondary" data-testid="prompt-diff-context-note">
              Consignes <strong>en service</strong>, telles que lues chez Mistral
              {fetchedAt ? ` le ${fetchedAt}` : ''}. Cet écran compare et montre : il ne modifie rien.
              {' '}L'agent de contexte, qui prépare le résumé de page, ne rend pas de verdict et n'est
              pas comparable ici.
            </Typography>

            {readable.length < 2 && (
              <Alert severity="warning">
                Moins de deux consignes lisibles : rien à comparer. Les entrées non comparables
                restent listées ci-dessous avec leur raison.
              </Alert>
            )}

            {this.renderCouples('Doivent se ressembler :', alike, 'prompt-diff-couple-alike')}
            {this.renderCouples(
              'Entre surfaces — écart attendu :',
              across,
              'prompt-diff-couple-across',
              'ces consignes ne sont pas mises en page pareil : le pourcentage n’est pas interprétable — seules la taxonomie des motifs et la règle de langue devraient concorder.'
            )}

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
              {this.renderSelect('prompt-diff-select-a', 'Consigne A', leftId, this.onLeftChange, options)}
              {this.renderSelect('prompt-diff-select-b', 'Consigne B', rightId, this.onRightChange, options)}
            </Stack>

            {diff ? (
              <Stack spacing={2} ref={this.tableRef} sx={{ flex: 1, minHeight: 0 }}>
                {this.renderSummary(diff)}
                {/* UNE SEULE zone défilante, sur les deux axes (le tableau est en largeur fixe et
                    replie ses lignes : l'axe horizontal n'est qu'un filet). Le corps de page, lui,
                    ne défile jamais horizontalement. `minHeight: 240` garde le tableau lisible sur
                    un écran bas. */}
                <Box data-testid="prompt-diff-body" sx={{ flex: 1, minHeight: 240, overflow: 'auto' }}>
                  {narrow ? this.renderNarrowTable(diff) : this.renderWideTable(diff)}
                </Box>
              </Stack>
            ) : (
              // ⚠ `open &&` : un Dialog MUI garde ses enfants montés pendant la transition de
              //   sortie (~195 ms) — sans ce garde, le fondu de fermeture afficherait cette
              //   alerte alors que deux consignes SONT sélectionnées.
              open &&
              readable.length >= 2 && (
                <Alert severity="info">Sélectionner deux consignes lisibles pour lancer la comparaison.</Alert>
              )
            )}
          </Stack>
        </DialogContent>
      </Dialog>
    );
  }
}

export default PromptDiff;
